import {
  HHButton,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHTypography,
} from '@hinge-health/react-component-library';
import { RuleStatus } from '../../hinge-connect/rules/types';

type StatusChangeConfirmationDialogProps = {
  open: boolean;
  currentStatus: RuleStatus;
  targetStatus: RuleStatus;
  ruleName: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const StatusChangeConfirmationDialog = ({
  open,
  currentStatus,
  targetStatus,
  ruleName,
  loading = false,
  onConfirm,
  onCancel,
}: StatusChangeConfirmationDialogProps): JSX.Element => {
  const getDialogContent = (): { title: string; message: string } => {
    switch (targetStatus) {
      case RuleStatus.Published:
        return {
          title: 'Publish Configuration?',
          message:
            'Are you sure you want to publish this configuration? It will become active and ready for use.',
        };
      case RuleStatus.Unpublished:
        return {
          title: 'Unpublish Configuration?',
          message:
            'This will make the configuration inactive and stop it from processing files.',
        };
      case RuleStatus.Draft:
        return {
          title: 'Set Configuration to Draft?',
          message:
            'This will move the configuration to draft status for further editing.',
        };
      default:
        return {
          title: 'Change Configuration Status?',
          message: 'Are you sure you want to change this configuration status?',
        };
    }
  };

  const { title, message } = getDialogContent();

  return (
    <HHDialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
    >
      <HHDialogTitle>{title}</HHDialogTitle>
      <HHDialogContent>
        <HHTypography
          hhVariant="body1"
          sx={{
            marginBottom: 2,
            color: 'text.primary',
          }}
        >
          {message}
        </HHTypography>
        <HHTypography
          hhVariant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          Configuration: <strong>{ruleName}</strong>
        </HHTypography>
        <HHTypography
          hhVariant="body2"
          sx={{
            color: 'text.secondary',
            marginTop: 1,
          }}
        >
          Status will change from <strong>{currentStatus}</strong> to{' '}
          <strong>{targetStatus}</strong>
        </HHTypography>
      </HHDialogContent>
      <HHDialogActions>
        <HHButton hhVariant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </HHButton>
        <HHButton
          hhVariant="contained"
          color={targetStatus === RuleStatus.Unpublished ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Confirm'}
        </HHButton>
      </HHDialogActions>
    </HHDialog>
  );
};
